import BaseModel from './Base.model';
import DemoCarModel from './DemoCar.model';
import ProductModel from './Product.model';

/**
 * Model đại diện cho một số khung số máy
 */
export default class ProductChassisModel extends BaseModel {
  static STATUS_CONFIG = {
    NEW: {
      text: 'Mới',
      class: 'label-light-success',
    },
    SALED: {
      text: 'Đã bán',
      class: 'label-light-primary',
    },
    TRANSFERING: {
      text: 'Đang vận chuyển',
      class: 'label-light-warning',
    },
    ERROR: {
      text: 'Lỗi',
      class: 'label-light-danger',
    },
    RETURN_TO_SUPPLIER: {
      text: 'Đã trả NCC',
      class: 'label-light-dark',
    },
    STOCK_TRANSFER: {
      text: 'Đang chuyển kho',
      class: 'label-light-warning',
    },
    EXPORT_WARRANTY: {
      text: 'Đã trả bảo hành',
      class: 'label-light-dark',
    },
    ON_WARRANTY: {
      text: 'Đang bảo hành',
      class: 'label-light-warning',
    },
    ON_HOLDING: {
      text: 'Đang giữ hàng',
      class: 'label-light-warning',
    },
    LENDING: {
      text: 'Đang cho mượn',
      class: 'label-light-warning',
    },
    LOSS: {
      text: 'Thất thoát',
      class: 'label-light-danger',
    },
    IN_DEMO: {
      text: 'Đang demo',
      class: 'label-light-info',
    },
  };

  /**
   * Tạo một instance mới của ProductChassis
   * @param {Object} data - Dữ liệu xe demo
   * @param {string} data.id - ID của xe demo
   * @param {string} data.storeId - ID của cửa hàng
   * @param {string} data.storeName - Tên cửa hàng
   * @param {string} data.productCode - Mã sản phẩm
   * @param {string} data.productName - Tên sản phẩm
   * @param {string} data.productId - Tên sản phẩm
   * @param {string} data.chassisNumber - Số khung
   * @param {string} data.engineNumber - Số máy
   * @param {string} data.licensePlate - Biển số xe
   * @param {number} data.status - Trạng thái của xe
   * @param {Date} data.lastMaintenanceDate - Ngày bảo dưỡng lần cuối
   * @param {number} data.currentKm - Km hiện tại
   * @param {string} data.notes - Ghi chú
   * @param {Date} data.createdAt - Ngày tạo
   * @param {Date} data.updatedAt - Ngày cập nhật
   * @param {string} data.createdBy - Người tạo
   * @param {string} data.updatedBy - Người cập nhật
   * @param {ProductModel} data.product - Thông tin sản phẩm
   * @param {Array<DemoCarModel>} data.demoTickets - Thông tin vé demo
   */
  constructor(data = {}) {
    super({
      id: data?.id,
      createAt: data?.createdAt,
      updateAt: data?.updatedAt,
    });
    this.storeId = data?.storeId || '';
    this.productId = data?.productId || '';
    this.chassisNumber = data?.chassisNumber || '';
    this.engineNumber = data?.engineNumber || '';
    this.providerId = data?.providerId || '';
    this.importPrice = data?.importPrice || 0;
    this.sellPrice = data?.sellPrice || 0;
    this.sellingPrice = data?.sellingPrice || 0;
    this.status = data?.status !== undefined ? data?.status : 0;
    this.deleted = data?.deleted !== undefined ? data?.deleted : 0;

    // Relations
    this.product = ProductModel.fromJson(data?.product);
    this.demoTickets = DemoCarModel.fromJsonArray(data?.demoTickets);

    // Additionals properties
    this.productName = this?.product?.name;
    this.productCode = this?.product?.code;
  }

  static STATUS = Object.fromEntries(
    Object.entries(this.STATUS_CONFIG).map(([key, value]) => [key, value.text]),
  );

  static getStatusText(status) {
    return this.STATUS_CONFIG[status]?.text || 'N/A';
  }

  static getStatusClass(status) {
    return this.STATUS_CONFIG[status]?.class || 'label-light-warning';
  }

  static getStatusConfig(status) {
    return (
      this.STATUS_CONFIG[status] || {
        text: 'N/A',
        class: 'label-light-waring',
      }
    );
  }
  /**
   * Chuyển đổi từ dữ liệu JSON thành đối tượng productChassis
   * @param {Object} json - Dữ liệu JSON
   * @returns {ProductChassisModel} - Đối tượng productChassis
   */
  static fromJson(json) {
    return BaseModel.fromJson(json, ProductChassisModel);
  }

  /**
   * Chuyển đổi từ danh sách JSON thành danh sách đối tượng productChassis
   * @param {Array} jsonArray - Mảng dữ liệu JSON
   * @returns {Array<ProductChassisModel>} - Mảng đối tượng productChassis
   */
  static fromJsonArray(jsonArray) {
    return BaseModel.fromJsonArray(jsonArray, ProductChassisModel);
  }

  /**
   * Chuyển đổi đối tượng productChassis thành dữ liệu JSON để gửi lên server
   * @returns {Object} - Dữ liệu JSON
   */
  toJson() {
    return {
      id: this.id,
      storeId: this.storeId,
      productId: this.productId,
      chassisNumber: this.chassisNumber,
      engineNumber: this.engineNumber,
      providerId: this.providerId,
      importPrice: this.importPrice,
      sellPrice: this.sellPrice,
      sellingPrice: this.sellingPrice,
      status: this.status,
      deleted: this.deleted,
    };
  }
}
